'use client';

import { useNavigate } from '@tanstack/react-router';
import { Cog, LogOut } from 'lucide-react';

import { StackLink } from '@/app/navigation/StackLink';
import { UserAvatar } from '@/components/ui/UserAvatar';
import { SyncStatusIndicator } from '@/features/offline/SyncStatusIndicator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/Sidebar/sidebar';
import { getSyncStatus } from '@/features/offline/swBridge';
import { useCurrentUser } from '@/features/users/users.hooks';
import AuthService from '@/services/AuthService';
import { useSidebar } from '@/components/ui/Sidebar/SidebarContextProps';

export function NavUser() {
  const { isMobile } = useSidebar();
  const navigate = useNavigate();
  const user = useCurrentUser();
  const handleLogout = async () => {
    // Sign-out wipes all offline data — warn when queued changes would be
    // lost (same rule as uninstalling the SW).
    const status = await getSyncStatus();
    const pending = status ? status.pendingUploads + status.pendingEvents : 0;
    if (
      pending > 0 &&
      !window.confirm(
        `${pending} offline change(s) have not been synced yet and will be LOST. Sign out anyway?`,
      )
    ) {
      return;
    }
    AuthService.getInstance().signOut();
    void navigate({ to: '/login' });
  };

  if (!user) {
    return null;
  }
  const displayName = user.name || user.loginname;

  return (
    <SidebarMenu>
      <SidebarMenuItem className="flex flex-row gap-2 group-data-[collapsible=icon]:flex-col">
        <SyncStatusIndicator />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent
                data-[state=open]:text-sidebar-accent-foreground"
            >
              <UserAvatar {...user} className="rounded-lg" size="md" />
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">{displayName}</span>
                <span className="truncate text-xs">@{user.loginname}</span>
              </div>
              {/* <ChevronsUpDown className="ml-auto size-4" /> */}
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            side={isMobile ? 'bottom' : 'right'}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <UserAvatar {...user} className="rounded-lg" size="md" />
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">{displayName}</span>
                  <span className="truncate text-xs">@{user.loginname}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem asChild>
                <StackLink to="/settings" stack={1}>
                  <Cog />
                  Settings
                </StackLink>
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => void handleLogout()}>
              <LogOut />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
